import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as _ from 'lodash';
import { UiService } from '../../../core/services/core.ui.service';
import { UserService } from '../../../core/services/core.user.service';
import { doGetPromise, doDeletePromise } from '../../../core/config/core.httpclient-utils';
import { GfTranslateService } from '../../../core/services/core.translate.service';
import { StateStackService } from '../../../core/services/core.state-stack.service';
import { TrackerRest} from './tracker.rest';
import { TrackerFieldsService } from './tracker.fields.service';
import { TrackerAclsService } from './tracker.acls.service';
import { TrackerWorkflowService } from './tracker.workflow.service';

@Injectable()
export class TrackerErrorsService {

    public set(_fields, _errors) {
        _.forEach(_errors, (_error, _key) => {
            if (_.has(_fields, _key)) {
                if (_error !== '') {
                    _fields[_key].hasError = true;
                    _fields[_key].error = _error;
                }
            }
        });
    }

    public reset(_fields) {
        _.forEach(_fields, (_field, _key) => {
            _fields[_key].hasError = false;
            _fields[_key].error = '';
        });
    }
}


@Injectable()
export class TrackerMassUpdateService {
    noChangeEFElement: any;
    massUpdateFieldTypes: any;
    
    constructor() {
        this.noChangeEFElement = { id: -1, alias: -1, label: '-- No Change --', defaultSelected: true, sortOrder: -1, statusId: 1 };
        this.massUpdateFieldTypes = [1, 2, 3, 5, 7, 8, 9, 12];
    }

    /**
     * Gives the map of EFs that are included in mass update
     * for the specified tracker.
     */
    public getDisplayFields(tracker: any) {
        var allowed = this.massUpdateFieldTypes;
        return _.pickBy(
            _.get(tracker, 'fields', {}),
            (ef) => {
                return(
                    _.get(ef, 'isEnabled', false) &&
                    _.includes(allowed, _.get(ef, 'fieldType', 0))
                );
            }
        );
    }

    /**
     * Insert the special "no change" element
     * into the specified field's elements, if it's needed.
     */
    public addNoChangeElement(field) {
        var ncElem = this.noChangeEFElement;
        if (Array.isArray(_.get(field, 'fieldElements', false))) {
            if (_.get(field, 'fieldElements[0].id', 0) > -1) {
                field.fieldElements = _.concat(
                    ncElem,
                    _.get(field, 'fieldElements', [])
                );
            }
        }
        return field;
    }

    /**
     * Creates a set of mass-update EFs for the specified tracker.
     */
    public getFieldOptions(tracker) {

        var types: any = this.massUpdateFieldTypes;
        var ret = _.pickBy(
            _.get(tracker, 'fields', {}),
            (ef) => {
                return types.includes(parseInt(_.get(ef, 'fieldType', 0), 10));
            }
        );

        for (var alias in ret) {
            ret[alias] = this.addNoChangeElement(ret[alias]);
            ret[alias].useWorkflow = false;  // No workflow filtering for mass update
        }

        return ret; 
    }
}

@Injectable()
export class TrackerService {
    fields: any;
    acl: any;
    errors: any;
    massUpdate: any;
    workflow: any;

    trackerTypes =  {
        TYPE_TRACKER: {
            id: 1,
            label: "Tracker"
        },
        TYPE_TASK: {
            id: 2,
            label: "Task"
        },
        TYPE_SUPPORT: {
            id: 3,
            label: "Support"
        }
    };

    constructor(
        private http: HttpClient, 
        private uiService: UiService, 
        private trackerRest: TrackerRest, 
        private gfTranslateService: GfTranslateService,
        private userService: UserService,
        private stateStackService: StateStackService
    ) {
        // Sets 'fields' namespace
        this.fields = new TrackerFieldsService(this.gfTranslateService);
        this.acl = new TrackerAclsService(this.userService, this.gfTranslateService);
        this.errors = new TrackerErrorsService();
        this.massUpdate = new TrackerMassUpdateService();
        this.workflow = new TrackerWorkflowService(this.http, this.uiService, this.fields);

        this.http = http;
    }

    private _prepareForSave(data) {
        var _data = _.cloneDeep(data);

        //_.assign(_data, data.settings);
        _data.isPublic = (_data.isPublic) ? 'Y' : 'N';
        _data.project = (_.isObject(data.project)) ? data.project.id : data.project;

        delete _data.fields;
        if (!_.isEmpty(_data.rel)) {
            delete _data.rel['monitoring'];
        }

        //Remove workflow settings
        if (_.has(_data, 'rel.workflow.fields')) {
            delete _data.rel.workflow;
        }

        return _data;
    }

    public load(trackerId) {
        return doGetPromise(this.http, `/api/tracker/${trackerId}/details/?rel=fields,assignees,workflow,monitoring,commit_actions,transition_scripts,settings`)
        .then((tracker) => {
            //Add field elements for priority, waiting for and lock status
            var priority = tracker?.fields?.priority ?? null;
            if (priority) {
                priority.fieldElements = this.fields.builtIn.priority.getList();
            }

            var waitingFor = tracker?.fields?.waitingFor ?? null;
            if (waitingFor) {
                waitingFor.fieldElements = this.fields.builtIn.waiting_for.getList();
            }

            var lockStatus = tracker?.fields?.lockStatus ?? null;
            if (lockStatus) {
                lockStatus.fieldElements = this.fields.builtIn.lock_status.getList();
            }

            return tracker;
        });
    }

    public loadAssignees(trackerId) {
        return doGetPromise(this.http, `/api/tracker/${trackerId}/assignees?page_size=100`)
    }

    public delete(tracker) {
        let trackerId: string = _.get(tracker, 'tracker_id', 0);
        
        return new Promise((resolve, reject) => {
            doDeletePromise(this.http, `/api/tracker/${trackerId}`)
            .then((response) => {
                this.uiService.message.showSuccess("Tracker " + tracker.tracker_name + " has been successfully deleted");
                resolve(response);
            }).catch((response) => {
                this.uiService.message.showError("There were one or more errors when deleting Tracker " + tracker.tracker_name);
                reject(response.data);
            });
        })
    }

    public create(data) {
        let trackerId: string = _.get(data, 'id', 0);

        return new Promise((resolve, reject) => {
            this.http.post(`/api/tracker/${trackerId}/details`, data)
            .toPromise()
            .then((response) => {
                this.uiService.message.showSuccess("Tracker " + data.trackerName + " has been successfully created");
                resolve(response);
            }).catch((response) => {
                this.uiService.message.showError("There were one or more errors when creating Tracker " + data.trackerName);
                reject(response);
            });
        });            
    }

    public save(data) {
        let trackerId: string = _.get(data, 'id', 0);

        return new Promise((resolve, reject) => {
            this.http.post(`/api/tracker/${trackerId}/details/?rel=fields,assignees,workflow,monitoring,commit_actions,transition_scripts,settings`, this._prepareForSave(data))
            .toPromise()
            .then((response) => {
                this.uiService.message.showSuccess("Tracker " + data.trackerName + " has been successfully saved");
                resolve(response);
            }).catch((response) => {
                this.uiService.message.showError("There were one or more errors when saving Tracker " + data.trackerName);
                reject(response);
            });
        }); 
    }

    public isSupportTracker(tracker) {
        var ret = (tracker.trackerType == this.trackerTypes.TYPE_SUPPORT.id);
        return (tracker.trackerType == this.trackerTypes.TYPE_SUPPORT.id);
    }

    public getTracker(stateParams, queries) {
        var id = _.get(stateParams, 'tracker', '');

        if (id == '') {
            id = _.get(queries, '[0].id', '');
        }

        if (id == '')  {
            return Promise.reject();
        }

        return new Promise((resolve, reject) => {
            this.load(id)
            .then((response) => {
                resolve(response);
            })
            .catch((result) => {
                reject(result);
            });
        });
    }

    /**
     * Given a TID, returns the members available for that TI
     * @param {int} tracker item id
     */
    public getMembers(tracker_id: number = 0) {
        return this.trackerRest.members(tracker_id)
        .then((data: any) => {
            return (_.has(data, 'items') ? data.items : []);
        });
    }

    public goToTracker(tracker) {
        var params = {
            unixName: tracker?.project?.unixName,
            tracker: tracker?.id
        };
        this.stateStackService.back('home.project.summary.tracker.browse', params);
    }

}
